import React, { useState, useCallback } from 'react';
import ConfirmDialog from './ConfirmDialog';
import colors from '../../theme/colors';
import client from '../../api/client';

// Each control: { key, label, type: 'toggle'|'number'|'select', reg, options?, min?, max?, step?, unit?, scale? }
export default function ControlPanel({ title, controls, buildDesired, currentValues = {} }) {
  const [values, setValues] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const hasChanges = Object.keys(values).length > 0;

  const handleChange = useCallback((key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setResult(null);
  }, []);

  const handleApply = async () => {
    setSending(true);
    setResult(null);
    try {
      const desired = buildDesired(values);
      await client.post('/api/shadow', { desired });
      setResult({ type: 'success', message: 'Command sent successfully' });
      setValues({});
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.error || 'Failed to send command' });
    } finally {
      setSending(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span className="card-title" style={{ marginBottom: 0 }}>{title}</span>
        <button
          onClick={() => setShowConfirm(true)}
          disabled={!hasChanges || sending}
          style={{
            padding: '6px 18px', borderRadius: 6, border: 'none',
            background: hasChanges ? colors.accent : colors.surface,
            color: hasChanges ? colors.bg : colors.textDim,
            fontSize: 12, fontWeight: 600, cursor: hasChanges ? 'pointer' : 'not-allowed',
          }}
        >
          Apply Changes
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
        {controls.map((ctrl) => (
          <ControlField
            key={ctrl.key}
            ctrl={ctrl}
            value={values[ctrl.key] ?? currentValues[ctrl.key]}
            changed={ctrl.key in values}
            onChange={(v) => handleChange(ctrl.key, v)}
          />
        ))}
      </div>

      {result && (
        <div style={{
          marginTop: 12,
          padding: '8px 12px',
          borderRadius: 6,
          fontSize: 12,
          background: result.type === 'success' ? 'rgba(81,207,102,0.12)' : 'rgba(255,107,107,0.12)',
          color: result.type === 'success' ? colors.success : colors.danger,
        }}>
          {result.message}
        </div>
      )}

      {showConfirm && (
        <ConfirmDialog
          title="Confirm Command"
          onConfirm={handleApply}
          onCancel={() => setShowConfirm(false)}
          confirmLabel={sending ? 'Sending...' : 'Send Command'}
        >
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 12 }}>
            The following changes will be sent:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(values).map(([key, val]) => {
              const ctrl = controls.find(c => c.key === key);
              if (!ctrl) return null;
              let displayVal = val;
              if (ctrl.type === 'toggle') {
                displayVal = val === 1 ? 'ON' : 'OFF';
              } else if (ctrl.type === 'select' && ctrl.options) {
                const opt = ctrl.options.find(o => o.value === val);
                displayVal = opt ? opt.label : val;
              } else if (ctrl.unit) {
                displayVal = `${val} ${ctrl.unit}`;
              }
              return (
                <div key={key} style={{
                  background: colors.surface,
                  padding: '8px 12px',
                  borderRadius: 6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span style={{ fontSize: 13, color: colors.textMuted }}>{ctrl.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: colors.accent, fontFamily: 'monospace' }}>{displayVal}</span>
                </div>
              );
            })}
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}

function ControlField({ ctrl, value, changed, onChange }) {
  const style = {
    background: colors.surface,
    borderRadius: 6,
    padding: '10px 12px',
    border: changed ? `1px solid ${colors.accent}` : `1px solid transparent`,
  };

  return (
    <div style={style}>
      <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 6, textTransform: 'uppercase' }}>
        {ctrl.label}
      </div>
      {ctrl.type === 'toggle' && (
        <ToggleSwitch value={value} onChange={onChange} />
      )}
      {ctrl.type === 'number' && (
        <NumberInput
          value={value}
          min={ctrl.min}
          max={ctrl.max}
          step={ctrl.step || 1}
          unit={ctrl.unit}
          onChange={onChange}
        />
      )}
      {ctrl.type === 'select' && (
        <SelectInput value={value} options={ctrl.options} onChange={onChange} />
      )}
    </div>
  );
}

function ToggleSwitch({ value, onChange }) {
  const isOn = value === 1 || value === true;
  return (
    <button
      onClick={() => onChange(isOn ? 0 : 1)}
      style={{
        padding: '4px 16px', borderRadius: 4,
        background: isOn ? colors.success : colors.surface,
        color: isOn ? '#fff' : colors.textMuted,
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
        border: `1px solid ${isOn ? colors.success : colors.cardBorder}`,
      }}
    >
      {isOn ? 'ON' : 'OFF'}
    </button>
  );
}

function NumberInput({ value, min, max, step, unit, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number"
        value={value ?? ''}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{
          width: 80, padding: '4px 8px',
          background: colors.card, border: `1px solid ${colors.cardBorder}`,
          borderRadius: 4, color: colors.text, fontSize: 13,
          fontFamily: 'monospace',
        }}
      />
      {unit && <span style={{ fontSize: 11, color: colors.textMuted }}>{unit}</span>}
    </div>
  );
}

function SelectInput({ value, options, onChange }) {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      style={{
        padding: '4px 8px',
        background: colors.card, border: `1px solid ${colors.cardBorder}`,
        borderRadius: 4, color: colors.text, fontSize: 13,
      }}
    >
      <option value="">--</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
